/* efx-studio-companion.js — T-CFG3, 2026-08-31.
 *
 * READ-ONLY. This file never writes to configurator state, never dispatches an event the
 * configurator listens to, never calls a configurator function, and never touches the order
 * path. It does exactly two things:
 *
 *   1. Copies text that the configurator has ALREADY rendered into the desktop product
 *      companion (ruling review-3-cfg-placement.md §2). Sources, all of them elements the
 *      wizard writes itself:
 *        #ef-price-display                        running total (ef-configurator-v2.js:3787)
 *        #efPd2StickyPriceLabel                   "Starting at" / "Your configuration"
 *        .ef-cfg-progress__header-fraction        "Step 2 of 6"
 *        .ef-cfg-progress__node[aria-current] label   current step name
 *        .ef-cfg-progress__node-summary           per-step choices (getStepSummary(), js:1038)
 *      The node summaries are hidden by CSS (T-CFG2A) but still written, so the build line
 *      "Masonry · 36"x28" · Bi-Fold · Matte Black" is real data, not a synthesis.
 *
 *   2. Toggles `body.efx-building` for the mobile focus mode (ruling §3.6) — a ground-colour
 *      change and nothing else. The listener is additive and never preventDefaults, so the
 *      CTA's own `href="#efCfg"` still does the scrolling and the wizard's scrollIntoView
 *      behaviour is untouched.
 *
 * Writes are confined to [data-efx-companion-*] nodes and document.body.classList.
 */
(function () {
  'use strict';

  var cfg = document.getElementById('efCfg');
  if (!cfg) return;

  /* ---------------------------------------------------------------- mirror */
  var elTotal = document.querySelector('[data-efx-companion-total]');
  var elLabel = document.querySelector('[data-efx-companion-label]');
  var elBuild = document.querySelector('[data-efx-companion-build]');
  var elStep = document.querySelector('[data-efx-companion-step]');

  function text(sel, root) {
    var el = (root || document).querySelector(sel);
    return el ? (el.textContent || '').trim() : '';
  }
  function set(el, value) {
    if (el && el.textContent !== value) el.textContent = value;
  }

  /* The rail's step label ships BOTH viewport strings in the same node —
     `<span class="ef-desktop-only">Measurements</span><span class="ef-mobile-only">Measure</span>`
     (ef-configurator-v2.js:998). textContent concatenates them into "MeasurementsMeasure", which
     is what the first pass of this file printed. Ask the CSS which one is actually rendered
     rather than guessing from the viewport width — the theme already owns that switch at
     efx-configurator.css:196. */
  function stepName() {
    var node = document.querySelector('.ef-cfg-progress__node[aria-current="step"] .ef-cfg-progress__node-label');
    if (!node) return '';
    var spans = node.querySelectorAll('.ef-desktop-only, .ef-mobile-only');
    for (var i = 0; i < spans.length; i++) {
      if (getComputedStyle(spans[i]).display !== 'none') return (spans[i].textContent || '').trim();
    }
    return (node.textContent || '').trim();
  }

  function sync() {
    if (elTotal) {
      var price = text('#ef-price-display');
      if (price) set(elTotal, price);
    }
    if (elLabel) {
      var label = text('#efPd2StickyPriceLabel');
      if (label) set(elLabel, label);
    }
    if (elStep) {
      var frac = text('.ef-cfg-progress__header-fraction');
      set(elStep, [frac, stepName()].filter(Boolean).join(' · '));
    }
    if (elBuild) {
      var parts = [];
      var nodes = cfg.querySelectorAll('.ef-cfg-progress__node-summary');
      for (var i = 0; i < nodes.length; i++) {
        var v = (nodes[i].textContent || '').trim();
        if (v && parts.indexOf(v) === -1) parts.push(v);
      }
      set(elBuild, parts.join(' · '));
    }
  }

  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; sync(); });
  }

  if (elTotal || elLabel || elBuild || elStep) {
    var opts = { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['aria-current'] };
    try { new MutationObserver(schedule).observe(cfg, opts); } catch (e) {}
    var priceEl = document.getElementById('ef-price-display');
    if (priceEl) { try { new MutationObserver(schedule).observe(priceEl, { childList: true, subtree: true, characterData: true }); } catch (e2) {} }
    var stickyLabel = document.getElementById('efPd2StickyPriceLabel');
    if (stickyLabel) { try { new MutationObserver(schedule).observe(stickyLabel, { childList: true, subtree: true, characterData: true }); } catch (e3) {} }
    /* The wizard also broadcasts these two; listening costs nothing and covers a price
       change that lands without a DOM mutation the observers happen to see. Read-only. */
    window.addEventListener('ef-cfg:price-changed', schedule);
    window.addEventListener('ef-cfg:state-changed', schedule);
    schedule();
    window.addEventListener('load', schedule, { once: true });
  }

  /* ----------------------------------------------------------- focus mode */
  /* Escape hatch, deliberately chosen (documented in logs/T-CFG3.md §5): the class comes
     off when the studio band is no longer intersecting the viewport — "leaving the studio
     by scrolling past it". No new button, no new tap target, nothing for a 40+ buyer to
     discover. Browser Back is unaffected because nothing is pushed onto the history stack. */
  var studio = document.querySelector('.efx-pdp__studio');
  var cta = document.querySelector('[data-efx-build]');
  if (studio && cta) {
    /* =================================================================== §R8-G4
       2026-09-03, logs/R8-G4-hotfix.md. THE ENTRY LATCH, and why it has to exist.

       R8-M8 (below) made this observer symmetric so the class would survive a reflow.
       That was right for the order bar and WRONG for everything else, because
       `body.efx-building` is not private to this file: assets/efx.js:36 reads it as one
       of the six guards that keep the mobile header PAINTED ("never hidden ... during the
       configurator build"). Under M8 the class meant only 'the studio band is on screen',
       so on the doors PDP at 390/412 it went true the moment a buyer scrolled into a
       1,600 px band -- and from there the header could never hide again. MEASURED on the
       draft, _work/R8-G4/diag1-390.json: at the gate's own anchor, g_building true,
       .efx-header.is-hidden absent, rail cssTop 64px, stuck false, rail top -20.3.
       Removing ONLY the class in-page and re-scrolling brought is-hidden back and dropped
       the rail to cssTop 0px -- one class, one cause (G4).

       The same toggle is the CLS: diag2.json measures the bar at 67 px without the class
       and 56 px with it, and the studio's min-height going 0 -> 844 px (document 9,409 ->
       9,455). Each entry/exit of the band scored 0.0191 against #efPd2Sticky, which is why
       a scroll pass that crosses the band ~18 times reported page-load CLS 0.3392.

       The fix is NOT to revert M8 -- its defect was real. It is to restore the ENTRY half
       of the meaning that M8 dropped: the class is 'the buyer is building AND the wizard is
       on screen'. `entered` latches on the three ways a build actually starts and is never
       cleared, so a reflow or a scroll excursion still re-adds the class exactly as M8
       intended; a buyer who merely scrolls past the band never sets it, and the header
       keeps the 64 px back. This also puts the doors companion back in step with
       efx-studio-mantel.js:100 and efx-studio-gaslogs.js:172, which never lost the entry
       condition. Read-only: capture phase, no preventDefault, no stopPropagation, no wizard
       node written, no configurator function called.
       ROLLBACK: delete `entered &&` on the add branch and the three listeners below. */
    var entered = false;
    cta.addEventListener('click', function () {
      entered = true;
      document.body.classList.add('efx-building');
    });
    /* The other two entries, both measured on the draft at 390 (diag2.json): the order
       bar's own "Build your door" is #efPd2StickyAtc and is NOT the [data-efx-build]
       anchor, so the click above never sees it; and a buyer can reach the wizard by
       scrolling and start by tapping a card. */
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      try { if (t.closest('#efPd2StickyAtc, .ef-pd2-sticky__atc, [data-efx-build], #efCfg')) entered = true; } catch (err) {}
    }, true);
    ['pointerdown', 'keydown', 'input', 'change'].forEach(function (ev) {
      document.addEventListener(ev, function (e) {
        var t = e.target;
        if (!t || !t.closest) return;
        try { if (t.closest('#efCfg')) entered = true; } catch (err) {}
      }, { capture: true, passive: true });
    });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          /* R8-M8, from logs/R8-M5-M6-M13.md: this observer USED TO ONLY REMOVE the class,
             so one reflow or scroll excursion permanently reverted the order bar to state (a)
             and gave the buyer the inert black button back on chapters 3-6 (measured at 390:
             true/true/false/false/false/false across the six chapters, bar 56 -> 67 px, ATC
             painted from chapter 3 on). The branch below is symmetric with the removal, so the
             class survives a reflow. §R8-G4 adds the `entered` term M8 dropped: symmetric for a
             buyer who has started a build, inert for one who is only scrolling past. */
          if (entries[i].isIntersecting) { if (entered) document.body.classList.add('efx-building'); }
          else document.body.classList.remove('efx-building');
        }
      }, { threshold: 0 }).observe(studio);
    }
  }
})();

/* ------------------------------------------------------- finish mirror (T-CFG3B)
 * Owner addendum 2: clicking a finish must swap the BIG companion image; the ~96 px
 * preview at the top of the step "klientowi nic nie daje".
 *
 * STILL READ-ONLY. This block selects nothing, dispatches nothing the configurator
 * listens to, and calls no configurator function. It reads a src the wizard has ALREADY
 * written and copies it into the companion.
 *
 * Source of truth, chosen after reading the wizard rather than guessing:
 *   selectSwatch() -> efCfgLivePreview(src, name)  (ef-configurator-v2.js:1999-2015)
 *   sets `lp.hidden = false`, writes the finish render into whichever of
 *   #efCfgFinishLiveImgA / #efCfgFinishLiveImgB is NOT currently shown, and cross-fades
 *   them by moving `.is-shown` — from an `onload` handler. #efCfgFinishLiveTag gets the
 *   finish name. Deselecting a swatch calls it with null, which sets `lp.hidden = true`.
 *
 * MEASURED TRAP, and the reason the first version of this file mirrored nothing
 * (logs/cfg3b-finish-t1.json, tools/cfg3b-dbg2.mjs). On desktop the live-preview pair is
 * painted `display:none` — it is a phone surface, added because "the gallery swap is
 * off-screen on phones" (js:1993) — and both <img> carry `loading="lazy"`. A lazy image
 * inside a display:none subtree is never fetched, so `next.onload` NEVER FIRES and
 * `.is-shown` never moves off image A. Measured after one swatch click at 1440:
 *     A  class "…__img is-shown"   src ""
 *     B  class "…__img"            src ".../ef-finish-rainbow-final-800.png?width=900"
 * Reading `.is-shown` therefore returns the EMPTY image on exactly the viewport where
 * this feature is for. The class is not a signal here; the src mutation is. So the URL is
 * taken from the mutation record itself, and `.is-shown` is used only as a tie-break for
 * a first paint that arrives with no mutation to observe.
 *
 * The other candidate was #efGallerySwiper's first slide, which efSwapMainImage() also
 * rewrites (js:1970). Rejected: it belongs to the gallery, the wizard restores it from
 * data-efOriginalSrc, and two readers of one mutable attribute is how you get a race.
 *
 * Roughly 40 % of door lines have no per-finish render at all (js:1993). For those the
 * wizard leaves the preview hidden, this block never fires, and the companion keeps the
 * product photo — the correct outcome, not a fallback.
 */
(function () {
  'use strict';

  var lp = document.getElementById('efCfgFinishLivePreview');
  var photo = document.querySelector('.efx-studio__photo');
  var img = photo && photo.querySelector('img');
  if (!lp || !photo || !img) return;

  var imgA = document.getElementById('efCfgFinishLiveImgA');
  var imgB = document.getElementById('efCfgFinishLiveImgB');

  var cap = document.createElement('span');
  cap.className = 'efx-studio__finish';
  photo.appendChild(cap);

  /* The template's own src/srcset, captured before anything is mirrored. Restoring BOTH
     matters: a stale srcset lets the browser re-pick the original at a different DPR and
     silently undo the restore. */
  var baseSrc = img.getAttribute('src') || '';
  var baseSrcset = img.getAttribute('srcset') || '';
  var baseAlt = img.getAttribute('alt') || '';

  var lastWritten = null;   /* set from the src mutation — see the trap above */
  var current = null;

  function tieBreak() {
    var el = (imgB && imgB.classList.contains('is-shown')) ? imgB : imgA;
    var s = el && el.getAttribute('src');
    if (s) return s;
    return (imgB && imgB.getAttribute('src')) || (imgA && imgA.getAttribute('src')) || null;
  }

  function shownSrc() {
    if (lp.hidden) return null;
    return lastWritten || tieBreak();
  }

  function apply() {
    var src = shownSrc();
    if (src === current) return;
    current = src;
    if (src) {
      /* The wizard has already appended width=900. The companion frame is 460 CSS px and
         renders at 2x on a retina desktop, so 900 is the right ask; re-transforming would
         only add a second CDN variant to fetch. */
      img.setAttribute('src', src);
      img.removeAttribute('srcset');
      var t = document.getElementById('efCfgFinishLiveTag');
      var n = t ? (t.textContent || '').trim() : '';
      cap.textContent = n;
      img.setAttribute('alt', n ? baseAlt + ' — ' + n + ' finish' : baseAlt);
      if (n) photo.setAttribute('data-efx-finish', n); else photo.removeAttribute('data-efx-finish');
      document.body.classList.add('efx-finish-mirrored');
    } else {
      img.setAttribute('src', baseSrc);
      if (baseSrcset) img.setAttribute('srcset', baseSrcset);
      img.setAttribute('alt', baseAlt);
      cap.textContent = '';
      photo.removeAttribute('data-efx-finish');
      document.body.classList.remove('efx-finish-mirrored');
    }
  }

  var pending = false;
  function queue() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () { pending = false; apply(); });
  }

  try {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var m = records[i];
        if (m.type === 'attributes' && m.attributeName === 'src' &&
            (m.target === imgA || m.target === imgB)) {
          var s = m.target.getAttribute('src');
          if (s) lastWritten = s;
        }
        if (m.type === 'attributes' && m.attributeName === 'hidden' && m.target === lp && lp.hidden) {
          lastWritten = null;
        }
      }
      queue();
    }).observe(lp, {
      attributes: true, attributeFilter: ['hidden', 'src', 'class'],
      childList: true, subtree: true, characterData: true
    });
  } catch (e) {}
  queue();
})();

/* ═══════════════════════════════════════════════════════ T5-10 — the receipt, the price
 * mirror, and two read-only prompts.
 *
 * SAME CONTRACT AS THE TWO IIFEs ABOVE. This block selects nothing, dispatches nothing the
 * configurator listens to, and calls no configurator function — with one deliberate,
 * spec-sanctioned exception: the `Edit` control forwards to the rail node's OWN click
 * handler (`railNode.click()`), which is the wizard's existing navigation and the only
 * approved call (ROUND-5-SPEC §3 T5-10). It writes to:
 *
 *   [data-efx-companion-receipt]   the six-row build receipt        (companion, change 1)
 *   [data-efx-companion-lines]     the mirror of #efCfgPriceLines   (companion, change 2)
 *   #efxRoundNote .hidden          one boolean, nothing else        (wizard,    change 4)
 *   document.body.classList        efx-receipt-built / efx-price-mirrored / efx-building
 *
 * `#efxRoundNote` is markup this task added to the snippet FOR this listener; its copy is
 * server-rendered so that with JS off the page is correct and merely silent. Flipping
 * `hidden` is the whole of the wizard-side write, and `hidden` is deliberately outside the
 * observer's attributeFilter below so the toggle cannot feed itself.
 *
 * Why the receipt is not synthesised: every value in it is the string the wizard itself
 * wrote into `.ef-cfg-progress__node-summary` via getStepSummary() (ef-configurator-v2.js),
 * painted nowhere at >=1200 because efx-configurator.css hides that node twice. The labels
 * are the rail's own node labels. Zero new data, and nothing that can disagree with the
 * wizard.
 *
 * Why the rows are built before they have values: a row that grows when its value arrives is
 * a layout shift on first paint. Every row is emitted at its full height from the first
 * render, with an empty value span, so filling it in moves nothing.
 * ═══════════════════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var cfg = document.getElementById('efCfg');
  if (!cfg) return;

  var elReceipt = document.querySelector('[data-efx-companion-receipt]');
  var elLines = document.querySelector('[data-efx-companion-lines]');
  var note = document.getElementById('efxRoundNote');
  var body = document.body;

  /* Same trap as stepName() above: the rail label ships the desktop and the mobile string in
     one node, so textContent concatenates them. Ask the CSS which one is painted. */
  function labelOf(node) {
    var l = node.querySelector('.ef-cfg-progress__node-label');
    if (!l) return '';
    var spans = l.querySelectorAll('.ef-desktop-only, .ef-mobile-only');
    for (var i = 0; i < spans.length; i++) {
      if (getComputedStyle(spans[i]).display !== 'none') return (spans[i].textContent || '').trim();
    }
    return (l.textContent || '').trim();
  }

  function visibleNodes() {
    var out = [];
    var all = cfg.querySelectorAll('.ef-cfg-progress__node');
    for (var i = 0; i < all.length; i++) {
      /* _isSkippedStep() hides whole chapters inline (cfg-screen skips Fireplace, Style and
         Glass). A receipt row for a chapter the buyer is never shown would be a lie. */
      if (all[i].style.display === 'none') continue;
      out.push(all[i]);
    }
    return out;
  }

  /* ------------------------------------------------------------------ 1. the receipt */
  var rows = [];
  var lastReceiptSig = null;

  function buildRows(n) {
    elReceipt.textContent = '';
    rows = [];
    for (var i = 0; i < n; i++) {
      var li = document.createElement('li');
      li.className = 'efx-receipt__row';
      var lab = document.createElement('span');
      lab.className = 'efx-receipt__label';
      var val = document.createElement('span');
      val.className = 'efx-receipt__value';
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'efx-receipt__edit';
      btn.textContent = 'Edit';
      btn.hidden = true;
      li.appendChild(lab);
      li.appendChild(val);
      li.appendChild(btn);
      elReceipt.appendChild(li);
      rows.push({ li: li, lab: lab, val: val, btn: btn });
    }
  }

  function renderReceipt() {
    if (!elReceipt) return;
    var nodes = visibleNodes();
    if (!nodes.length) return;

    var sig = [];
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      var s = n.querySelector('.ef-cfg-progress__node-summary');
      var state = n.classList.contains('is-done') ? 'd' : (n.classList.contains('is-active') ? 'a' : 'f');
      sig.push(n.dataset.step + '|~|' + labelOf(n) + '|~|' + (s ? (s.textContent || '').trim() : '') + '|~|' + state);
    }
    var joined = sig.join('|;|');
    if (joined === lastReceiptSig) return;
    lastReceiptSig = joined;

    if (rows.length !== nodes.length) buildRows(nodes.length);

    var filled = 0;
    for (i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      var sum = node.querySelector('.ef-cfg-progress__node-summary');
      var value = sum ? (sum.textContent || '').trim() : '';
      var done = node.classList.contains('is-done');
      var active = node.classList.contains('is-active');
      var r = rows[i];
      var label = labelOf(node);
      if (r.lab.textContent !== label) r.lab.textContent = label;
      if (r.val.textContent !== value) r.val.textContent = value;
      r.li.className = 'efx-receipt__row' + (done ? ' is-done' : (active ? ' is-current' : ' is-future'));
      /* Edit only where there is something to return to. A future chapter is reached by the
         rail or by Next, both of which run the wizard's own validation. */
      r.btn.hidden = !done;
      r.btn.setAttribute('data-efx-receipt-edit', node.dataset.step);
      r.btn.setAttribute('aria-label', 'Edit ' + (label || 'this step').toLowerCase());
      if (value) filled++;
    }
    if (filled > 0) body.classList.add('efx-receipt-built');
    else body.classList.remove('efx-receipt-built');
  }

  if (elReceipt) {
    elReceipt.addEventListener('click', function (e) {
      var t = e.target;
      while (t && t !== elReceipt && !(t.getAttribute && t.getAttribute('data-efx-receipt-edit') !== null)) t = t.parentNode;
      if (!t || t === elReceipt) return;
      var idx = t.getAttribute('data-efx-receipt-edit');
      var target = cfg.querySelector('.ef-cfg-progress__node[data-step="' + idx + '"]');
      /* The rail node's own listener (ef-configurator-v2.js) does the navigating, including
         its backwards/forwards validation. Nothing is reimplemented here. */
      if (target) target.click();
    });
  }

  /* ---------------------------------------------------------------- 2. the price mirror */
  var lastLineSig = null;

  function renderLines() {
    if (!elLines) return;
    var src = document.getElementById('efCfgPriceLines');
    var srcRows = src ? src.querySelectorAll('.ef-cfg-price-line') : [];
    var data = [];
    for (var i = 0; i < srcRows.length; i++) {
      var spans = srcRows[i].querySelectorAll('span');
      var label = spans[0] ? (spans[0].textContent || '').trim() : '';
      var vEl = srcRows[i].querySelector('.ef-cfg-price-line__value');
      var value = vEl ? (vEl.textContent || '').trim() : (spans[1] ? (spans[1].textContent || '').trim() : '');
      /* The wizard prefixes a surcharge label with "+ " because its own value column is the
         narrow one. Here the value column already carries the sign, so the duplicate "+"
         comes off the label. Nothing else about the string is touched. */
      if (label.slice(0, 2) === '+ ') label = label.slice(2);
      if (label || value) data.push([label, value, srcRows[i].className.indexOf('--surcharge') !== -1]);
    }
    var sig = JSON.stringify(data);
    if (sig !== lastLineSig) {
      lastLineSig = sig;
      elLines.textContent = '';
      for (i = 0; i < data.length; i++) {
        var row = document.createElement('div');
        row.className = 'efx-studio__line' + (data[i][2] ? ' efx-studio__line--surcharge' : '');
        var a = document.createElement('span');
        a.className = 'efx-studio__line-label';
        a.textContent = data[i][0];
        var b = document.createElement('span');
        b.className = 'efx-studio__line-value';
        b.textContent = data[i][1];
        row.appendChild(a);
        row.appendChild(b);
        elLines.appendChild(row);
      }
    }
    /* The in-wizard sidebar is hidden by CSS keyed on this class, at >=1200 only. The class
       goes on ONLY when the copy exists, and comes straight back off if it ever stops
       existing — the original is never hidden before its replacement is on screen. */
    if (data.length) body.classList.add('efx-price-mirrored');
    else body.classList.remove('efx-price-mirrored');
  }

  /* --------------------------------------------------------- 3. both-round-numbers prompt */
  var wIn = document.getElementById('efCfgWidth');
  var hIn = document.getElementById('efCfgHeight');

  /* A fraction is never a round number, whatever parseFloat makes of "36 1/8" (it makes 36).
     Decimals are honoured: 36.5 is not round, 36.0 is. */
  function whole(v) {
    v = (v || '').trim();
    if (!v) return false;
    if (v.indexOf('/') !== -1) return false;
    var n = parseFloat(v);
    return isFinite(n) && n > 0 && n % 1 === 0;
  }
  function checkRound() {
    if (!note) return;
    var hit = whole(wIn && wIn.value) && whole(hIn && hIn.value);
    if (note.hidden === !hit) return;
    note.hidden = !hit;
  }
  if (note && (wIn || hIn)) {
    ['input', 'change', 'blur'].forEach(function (ev) {
      if (wIn) wIn.addEventListener(ev, checkRound);
      if (hIn) hIn.addEventListener(ev, checkRound);
    });
  }

  /* ------------------------------------------------------ 4. the exit from focus mode */
  var exit = document.querySelector('[data-efx-exit-build]');
  if (exit) {
    /* Additive, never preventDefault: the anchor's own href="#efGallerySwiper" does the
       scrolling, with or without this listener. All this does is leave the mode. */
    exit.addEventListener('click', function () { body.classList.remove('efx-building'); });
  }

  /* ------------------------------------------------------------------------ scheduling */
  var queued = false;
  function tick() {
    queued = false;
    renderReceipt();
    renderLines();
    checkRound();
  }
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(tick);
  }

  if (elReceipt || elLines || note) {
    try {
      /* `hidden` is intentionally absent from attributeFilter: it is the one attribute this
         block writes inside #efCfg, and observing it would make the toggle re-enter. */
      new MutationObserver(schedule).observe(cfg, {
        childList: true, subtree: true, characterData: true,
        attributes: true, attributeFilter: ['class', 'aria-current', 'style'],
      });
    } catch (e) {}
    window.addEventListener('ef-cfg:price-changed', schedule);
    window.addEventListener('ef-cfg:state-changed', schedule);
    window.addEventListener('resize', schedule, { passive: true });
    schedule();
    window.addEventListener('load', schedule, { once: true });
  }
})();

/* ═══════════════════════════════════════════════════════ T6-B — two read-only mirrors of
 * chapter 1: the measuring-instructions tab, and which overlap sentence is true.
 *
 * SAME CONTRACT AS THE THREE IIFEs ABOVE. It selects nothing, dispatches nothing, calls no
 * configurator function at all (not even railNode.click(), which T5-10 was allowed), and
 * reads the wizard only through `aria-checked` on its option cards. Everything it writes is
 * markup T6-B added to the snippet for this listener:
 *
 *   #efxMmMasonry / #efxMmPrefab   .checked          the teaching tab's default
 *   [data-fitline]                 .hidden           one boolean per sentence
 *
 * Neither write can feed a loop: `.checked` is an IDL property and produces no mutation
 * record at all, and `hidden` is outside every attributeFilter in this file (T5-10 §2.2
 * made the same guarantee for #efxRoundNote).
 *
 * Both surfaces are server-rendered correct without JS: the tab opens on Masonry with the
 * other tab visibly available, and the fit list shows the hole-in-the-wall sentence, which
 * is the default ROUND-6-SPEC D3 set until Q71 is answered. With JS off the page is
 * incomplete, never wrong.
 *
 * The tab is a teaching control, not a field. Once the buyer clicks a tab himself this
 * block stops setting it — a reader who asked for the masonry method must not have it
 * snatched back when he returns to chapter 1 and changes his mind about something else.
 * ═══════════════════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var cfg = document.getElementById('efCfg');
  if (!cfg) return;

  var tabMasonry = document.getElementById('efxMmMasonry');
  var tabPrefab = document.getElementById('efxMmPrefab');
  var fitLines = cfg.querySelectorAll('[data-fitline]');
  if (!tabMasonry && !tabPrefab && !fitLines.length) return;

  /* The buyer's own click wins from the moment it happens. */
  var userPicked = false;
  [tabMasonry, tabPrefab].forEach(function (el) {
    if (!el) return;
    el.addEventListener('change', function () { userPicked = true; });
  });

  /* The selected card's title in one group, or '' — the same read T5-10's stepName() does,
     one level down. Never writes, never clicks. */
  function chosen(group) {
    var g = cfg.querySelector('.ef-cfg-group[data-group="' + group + '"]');
    if (!g) return '';
    var card = g.querySelector('.ef-cfg-card[aria-checked="true"]');
    if (!card) return '';
    var t = card.querySelector('.ef-cfg-card__title');
    return ((t ? t.textContent : card.textContent) || '').trim();
  }

  /* 1. the teaching tab follows chapter 1's Fireplace Type, until the buyer overrides it.
        The wizard's own two options are "Masonry" and "Prefab (Factory-Built)"; both
        spellings are matched so a relabelled option cannot silently teach the wrong
        method. An unrecognised label leaves the server-rendered default alone. */
  function syncMode() {
    if (userPicked || !tabMasonry || !tabPrefab) return;
    var v = chosen('fireplace_type').toLowerCase();
    if (!v) return;
    if (/masonry|brick|stone/.test(v)) { if (!tabMasonry.checked) tabMasonry.checked = true; }
    else if (/prefab|factory|metal|zero.?clearance/.test(v)) { if (!tabPrefab.checked) tabPrefab.checked = true; }
  }

  /* 2. the overlap sentence follows chapter 1's Firebox Type. The wizard ships exactly two
        options — "Flush Hearth" ("Firebox floor flush with hearth") and "Hole in Wall"
        ("Firebox floor greatly higher than hearth") — and with a flush hearth there is no
        masonry below the opening for the frame to overlap onto: the bottom rests on the
        hearth. Exactly one of the two <li> is visible at any moment. */
  function syncFitLine() {
    if (!fitLines.length) return;
    var v = chosen('firebox_type').toLowerCase();
    var mode = /flush/.test(v) ? 'flush' : 'hole';   /* hole is the default, per D3 */
    for (var i = 0; i < fitLines.length; i++) {
      var el = fitLines[i];
      var want = el.getAttribute('data-fitline') !== mode;
      if (el.hidden !== want) el.hidden = want;
    }
  }

  var queued = false;
  function tick() { queued = false; syncMode(); syncFitLine(); }
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(tick);
  }

  try {
    /* Only aria-checked: this block has no interest in class, style or text, and narrowing
       the filter keeps it off the hot path the wizard walks on every keystroke. */
    new MutationObserver(schedule).observe(cfg, {
      childList: true, subtree: true, attributes: true, attributeFilter: ['aria-checked'],
    });
  } catch (e) {}
  window.addEventListener('ef-cfg:state-changed', schedule);
  schedule();
  window.addEventListener('load', schedule, { once: true });
})();


/* =====================================================================
   §R8-M10 — TEXT MIRROR: one label on the review receipt. 2026-09-03.
   ROLLBACK: delete from this banner to EOF.

   The frozen wizard's buildReview() writes the row
   `Opening Size: Medium: 60-70` (ef-configurator-v2.js:4004, from
   `selections.size`). `Medium: 60-70` is not an opening - it is the PRICE BRACKET
   the door falls into, and the buyer's real opening is already two rows above it
   as `Width 36"` and `Height 28"`. A receipt that prints a price band under the
   word "Opening" tells the buyer something untrue about the thing he just
   measured, and he is about to pay against that receipt.

   This relabels the row's KEY to `Size bracket`. It does not touch the value, does
   not compute a value, does not touch any other row, and does not touch the cart:
   the line-item property is still `Opening Size` (frozen JS :4705 / :6189) -
   changing that is an order-data change and belongs to whoever owns the frozen
   file, not to a display mirror. Recorded as an open item in the M10 log.

   `reviewStepMap` is read at build time by label, so the Edit link's
   `data-edit-step` is resolved before this runs and cannot be disturbed by a later
   text change; only the link's aria-label is brought along, so a screen reader
   hears the same word the eye reads.

   Write surface: the textContent of one <span> and one aria-label. No wizard
   state, no event the wizard listens to, no configurator function. */
(function () {
  'use strict';
  var grid = document.getElementById('efCfgReviewGrid');
  if (!grid) return;

  var FROM = 'Opening Size';
  var TO = 'Size bracket';

  function relabel() {
    var labels = grid.querySelectorAll('.ef-cfg-review-item__label');
    for (var i = 0; i < labels.length; i++) {
      var el = labels[i];
      /* the guard that makes this terminate: after the write the text no longer
         matches, so the mutation the write itself causes is a no-op pass. */
      if ((el.textContent || '').trim() !== FROM) continue;
      el.textContent = TO;
      var row = el.parentNode;
      var edit = row && row.querySelector('.ef-cfg-review-item__edit');
      if (edit && /opening size/i.test(edit.getAttribute('aria-label') || '')) {
        edit.setAttribute('aria-label', 'Edit size bracket');
      }
    }
  }

  try {
    new MutationObserver(relabel).observe(grid, { childList: true, subtree: true });
  } catch (e) {}
  relabel();
})();


/* =====================================================================
   §R8-M12 + §R8-M14 — rail auto-centre, and the three events nothing else emits.
   2026-09-03. ROLLBACK: delete from this banner to EOF.

   ---------------------------------------------------------------- M12
   THE SPEC'S PREMISE IS STALE AND THE MEASUREMENT SAYS SO.
   ROUND-8-SPEC §M12 was written against the pre-T-CFG3 rail ("with 3.5 nodes visible a
   buyer on chapter 5 otherwise sees nodes 1-3.5"). T-CFG3 §4 has since made the six nodes
   `flex: 1 1 0; min-width: 0` at <=599 on `.efx-pdp #efCfg`, and the rail measured on this
   draft at 390 and 375 does not overflow at all: scrollWidth === clientWidth (358/358 at
   390, 343/343 at 375), scrollLeft 0 on every chapter. There is currently nothing to
   centre, and this block is INERT by measurement, not by accident.

   It ships anyway, guarded, because the guard is the rule: if any tier, width or label
   change ever makes the node row wider than its box again, the active node is centred
   instead of hiding off the right edge. Verified inert (see logs/R8-M12-M14.md).

   IT DOES NOT USE node.scrollIntoView({inline:'center'}), and that is the point.
   scrollIntoView acts on the nearest scrollable ANCESTOR chain: when the rail does not
   overflow — which is its state on this draft, always — the call escapes upward to the
   document and scrolls the PAGE, moving the chapter out from under the buyer's thumb.
   That is the exact risk §M12 named, and here it is not hypothetical. Writing scrollLeft
   on the rail's own scroller is arithmetically identical to `inline:'center'` and is
   structurally incapable of touching window.scrollY.

   ---------------------------------------------------------------- M14
   The frozen wizard ALREADY emits and ALREADY gtag-forwards four events (efTrack,
   ef-configurator-v2.js:134-149): ef_cfg_step_view, ef_cfg_option_select, ef_cfg_price,
   ef_cfg_atc. Measured: 32 pushes / 32 paired gtag calls / 0 unpaired on a full walk.
   They are NOT re-emitted here — duplicating them would double every funnel number in the
   read that ROUND-8-SPEC §4 is built on. cfg_chip_tap is already produced by
   efx-cfg-diagram.js §R8-M8. What is left, and what this block adds, is the three the
   spec asks for that no producer emits at all:
       cfg_call_click   (tel + Calendly)      cfg_save_click   (the five save surfaces)
       cfg_review_view  (once, on reaching review)
   cfg_type / device / product_handle / step_name are added by snippets/efx-analytics-forward.liquid
   to every ef_* / cfg_* push, so they are not repeated here.

   WRITE SURFACE: one property on one scroller (scrollLeft, via scrollTo) and
   window.dataLayer. No wizard node is written, no wizard function is called, no event the
   wizard listens to is dispatched, and every listener is capture-phase and read-only —
   nothing here calls preventDefault or stopPropagation, so no tap it observes is changed. */
(function () {
  'use strict';
  var cfg = document.getElementById('efCfg');
  if (!cfg) return;

  /* ---------------------------------------------------------------- shared emit */
  function emit(name, params) {
    try {
      window.dataLayer = window.dataLayer || [];
      var p = params || {};
      p.event = name;
      window.dataLayer.push(p);
    } catch (e) {}
  }

  /* ---------------------------------------------------------------- M12 */
  var mqLane = null, mqMotion = null;
  try {
    mqLane = window.matchMedia('(max-width: 1023px)');
    mqMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  } catch (e) {}

  var lastNode = null;

  function centreRail() {
    var node = cfg.querySelector('.ef-cfg-progress__node[aria-current="step"]');
    if (!node || node === lastNode) return;
    lastNode = node;
    if (mqLane && !mqLane.matches) return;              /* desktop rail is its own lane */
    var host = node.parentElement;                       /* .ef-cfg-progress__nodes, and only it */
    if (!host) return;
    var max = host.scrollWidth - host.clientWidth;
    if (max <= 2) return;                                /* nothing to centre — the rail fits */
    var target = node.offsetLeft + (node.offsetWidth / 2) - (host.clientWidth / 2);
    if (target < 0) target = 0; else if (target > max) target = max;
    if (Math.abs(target - host.scrollLeft) < 1) return;
    try {
      host.scrollTo({ left: target, behavior: (mqMotion && mqMotion.matches) ? 'auto' : 'smooth' });
    } catch (e) {
      host.scrollLeft = target;                          /* older engines: no options object */
    }
  }

  /* ---------------------------------------------------------------- M14: review view */
  var reviewSent = false;
  function checkReview() {
    if (reviewSent) return;
    var r = document.getElementById('efCfgStepReview');
    if (!r || !r.classList.contains('is-visible')) return;
    reviewSent = true;
    emit('cfg_review_view', {});
  }

  /* one observer for both — the rail's aria-current and the step's class both live under
     #efCfg and both change on the same chapter transition. */
  try {
    new MutationObserver(function () { centreRail(); checkReview(); })
      .observe(cfg, { attributes: true, attributeFilter: ['class', 'aria-current'], subtree: true });
  } catch (e) {}
  lastNode = cfg.querySelector('.ef-cfg-progress__node[aria-current="step"]');  /* no centring on load */
  checkReview();

  /* ---------------------------------------------------------------- M14: call + save */
  var SAVE = {
    efSvOpen: 'local',            /* "Save & finish later" chip */
    efSvCopy: 'copy_link',
    efSvShare: 'share',
    efSvSend: 'email',
    efCfgEmailConfig: 'email_link' /* "Save or share configuration" on the review step */
  };

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    try {
      var tel = t.closest('a[href^="tel:"]');
      if (tel) {
        emit('cfg_call_click', { kind: 'phone', in_cfg: !!cfg.contains(tel) });
        return;
      }
      var cal = t.closest('[data-efx-calendly], a[href*="calendly.com"], [data-calendly]');
      if (cal) {
        emit('cfg_call_click', { kind: 'calendly', in_cfg: !!cfg.contains(cal) });
        return;
      }
      var sv = t.closest('#efSvOpen, #efSvCopy, #efSvShare, #efSvSend, #efCfgEmailConfig');
      if (sv && SAVE[sv.id]) emit('cfg_save_click', { kind: SAVE[sv.id] });
    } catch (err) {}
  }, true);
}());
